import type { VoucherCodeResponse } from '@/api/model/voucherCodeResponse';
import type { VoucherResponse } from '@/api/model/voucherResponse';
import { Card, CardHeader } from '@/components/ui/card';
import { MyVoucherGridContainer } from '@/components/vouchers/MyVoucherGridContainer';
import { OwnedVoucherModal } from '@/components/vouchers/OwnedVoucherModal';
import { VoucherDetailsModal } from '@/components/vouchers/VoucherDetailsModal';
import type { VoucherSortOrder } from '@/components/vouchers/VoucherFilterControls';
import { useState } from 'react';
import { MyVoucherFilterBar, type VoucherStatusFilter } from './MyVoucherFilterBar';

export const MyVouchersPage = () => {
  const [selectedCode, setSelectedCode] = useState<VoucherCodeResponse | null>(null);
  const [selectedBookmarkedVoucher, setSelectedBookmarkedVoucher] = useState<VoucherResponse | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBookmarkedModalOpen, setIsBookmarkedModalOpen] = useState(false);

  // Filter states
  const [selectedCategory, setSelectedCategory] = useState('alle');
  const [searchQuery, setSearchQuery] = useState('');
  const [voucherType, setVoucherType] = useState<'activated' | 'bookmarked'>('activated');
  const [selectedStatus, setSelectedStatus] = useState<VoucherStatusFilter>('all');
  const [sortOrder, setSortOrder] = useState<VoucherSortOrder>('newest');

  const handleVoucherClick = (code: VoucherCodeResponse) => {
    setSelectedCode(code);
    setIsModalOpen(true);
  };

  const handleBookmarkedVoucherClick = (voucher: VoucherResponse) => {
    setSelectedBookmarkedVoucher(voucher);
    setIsBookmarkedModalOpen(true);
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const handleSortChange = (order: VoucherSortOrder) => {
    setSortOrder(order);
  };

  const handleVoucherTypeChange = (type: 'activated' | 'bookmarked') => {
    setVoucherType(type);
  };

  const handleStatusChange = (status: VoucherStatusFilter) => {
    setSelectedStatus(status);
  };

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='text-center'>
        <h1 className='text-h1'>Meine Gutscheine</h1>
        <p className='text-body-2 mt-2'>Verwalten Sie Ihre gekauften und vorgemerkten Gutscheine</p>
      </div>

      {/* Layout Card wrapping filter bar, grid, and pagination */}
      <Card className='rounded-[16px] shadow-xs'>
        <CardHeader className='p-4 sm:p-10'>
          <MyVoucherFilterBar
            selectedCategory={selectedCategory}
            searchQuery={searchQuery}
            voucherType={voucherType}
            selectedStatus={selectedStatus}
            onCategoryChange={handleCategoryChange}
            onSearchChange={handleSearchChange}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
            onVoucherTypeChange={handleVoucherTypeChange}
            onStatusChange={handleStatusChange}
          />
        </CardHeader>

        <MyVoucherGridContainer
          selectedCategory={selectedCategory}
          searchQuery={searchQuery}
          voucherType={voucherType}
          selectedStatus={selectedStatus}
          sortOrder={sortOrder}
          onVoucherClick={handleVoucherClick}
          onBookmarkedVoucherClick={handleBookmarkedVoucherClick}
        />
      </Card>

      <OwnedVoucherModal code={selectedCode} open={isModalOpen} onOpenChange={setIsModalOpen} />

      <VoucherDetailsModal
        voucher={selectedBookmarkedVoucher}
        open={isBookmarkedModalOpen}
        onOpenChange={setIsBookmarkedModalOpen}
      />
    </div>
  );
};
